import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Target, Heart, Users, Award, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const TEAM_IMAGE = "https://images.unsplash.com/photo-1714176966820-d5d9baee2ca6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwdm9sdW50ZWVyJTIwZ3JvdXAlMjB0ZWFtJTIwYXRobGV0aWNzJTIwbm9ucHJvZml0fGVufDB8fHx8MTc3MjU2MjcyM3ww&ixlib=rb-4.1.0&q=85";

const values = [
  {
    icon: Target,
    title: 'Equity',
    description: 'We believe every child deserves equal access to athletic opportunities, regardless of financial circumstances.',
  },
  {
    icon: Heart,
    title: 'Compassion',
    description: 'We approach every family with understanding and dignity, recognizing the courage it takes to ask for help.',
  },
  {
    icon: Users,
    title: 'Community',
    description: 'We build connections between athletes, families, coaches, and supporters to create lasting impact.',
  },
  {
    icon: Award,
    title: 'Excellence',
    description: 'We maintain the highest standards in everything we do, ensuring resources reach those who need them most.',
  },
];

const timeline = [
  { year: '2024', event: 'Foundation established with a mission to support youth athletics' },
  { year: '2024', event: 'First equipment fund grants distributed to local athletes' },
  { year: '2025', event: 'Expanded to registration assistance and training programs' },
  { year: '2026', event: 'Growing nationally with partners across multiple states' },
];

const About = () => {
  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-red-600 font-bold uppercase tracking-widest text-sm">About Us</span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mt-4">
                Our Story
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                Lane 4 For All Kids Foundation was born from a simple observation: too many talented young athletes were sitting on the sidelines, not because they lacked ability, but because they lacked resources.
              </p>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                We're changing that. One athlete at a time, one community at a time, we're working to ensure that financial barriers never prevent a child from pursuing their athletic dreams.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={TEAM_IMAGE}
                  alt="Team working together"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-red-600 font-bold uppercase tracking-widest text-sm">Our Mission</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-4">
              To eliminate financial barriers in youth athletics and create equitable opportunities for children to participate, compete, and thrive.
            </h2>
            <div className="mt-8 w-24 h-1 bg-red-600 mx-auto" />
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-32 bg-slate-900 text-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-500 font-bold uppercase tracking-widest text-sm">Our Values</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mt-4">
              What Guides Us
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-6 border border-slate-700 hover:border-red-500 transition-colors"
                data-testid={`value-card-${index}`}
              >
                <value.icon className="w-10 h-10 text-red-500 mb-4" />
                <h3 className="font-heading text-xl font-bold">{value.title}</h3>
                <p className="mt-3 text-slate-400">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="timeline-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-600 font-bold uppercase tracking-widest text-sm">Our Journey</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-4">
              Growing Together
            </h2>
          </div>
          <div className="max-w-3xl mx-auto">
            {timeline.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-start space-x-6 mb-8 last:mb-0"
                data-testid={`timeline-item-${index}`}
              >
                <div className="flex-shrink-0 w-20 text-right">
                  <span className="font-heading text-2xl font-bold text-red-600">{item.year}</span>
                </div>
                <div className="flex-shrink-0 w-4 h-4 bg-red-600 rounded-full mt-2" />
                <div className="flex-grow">
                  <p className="text-lg text-slate-700">{item.event}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Lane 4 Name Section */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-red-600 font-bold uppercase tracking-widest text-sm">The Name</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-4">
              Why "Lane 4"?
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              In competitive track and field, Lane 4 is considered the most advantageous position. It's where the fastest qualifier typically runs—the prime lane with the best view of the competition and the straightest path to victory.
            </p>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              We named our foundation Lane 4 because we believe every child deserves that prime position. Regardless of their family's financial circumstances, every young athlete should have access to the same opportunities as their peers—the chance to compete, grow, and succeed.
            </p>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              When we help a child participate in athletics, we're not just providing equipment or covering fees. We're giving them a lane to run in—a chance to discover their potential and pursue their dreams.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-24 bg-red-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            Join Our Mission
          </h2>
          <p className="mt-4 text-xl text-red-100 max-w-2xl mx-auto">
            Whether you donate, volunteer, or spread the word—you can help ensure every child has a lane to run in.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/donate" data-testid="about-donate-btn">
              <Button className="bg-white text-red-600 hover:bg-slate-900 hover:text-white rounded-none uppercase tracking-widest font-bold px-8 py-6 text-base">
                Donate Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/get-involved" data-testid="about-volunteer-btn">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-red-600 rounded-none uppercase tracking-widest font-bold px-8 py-6 text-base">
                Get Involved
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
